// ============================================================================
// PÁGINA: PREMIAÇÕES E EVENTOS (PUBLICAÇÕES)
// Arquivo: src/pages/publicacoes/PremiacoesEventos.tsx
// Descrição: Cards + busca + filtro por ano/tipo + detalhe em modal (mock)
// Ícones: public/icons (sem fundo)
// ============================================================================

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, Search, Filter, MapPin, Calendar } from "lucide-react";

type EventoTipo = "Premiação" | "Congresso" | "Capacitação" | "Encontro" | "Outro";

type Evento = {
  id: string;
  titulo: string;
  tipo: EventoTipo;
  dataInicio: string; // ISO-like "2026-03-10"
  dataFim?: string;
  cidade?: string;
  estado?: string;
  local?: string;
  resumo: string;
  imagemUrl?: string; // banner opcional
  linkExterno?: string;
  ano: number;
};

const eventosMock: Evento[] = [
  {
    id: "EVT-2026-001",
    titulo: "Premiação Melhores do Ano",
    tipo: "Premiação",
    dataInicio: "2026-03-10",
    cidade: "São Paulo",
    estado: "SP",
    local: "Auditório Principal",
    resumo:
      "Reconhecimento de profissionais e escolas com destaque nacional. Cerimônia com entrega de títulos e homenagens.",
    ano: 2026,
    linkExterno: "https://www.federacaodoscabeleireiros.com.br/",
  },
  {
    id: "EVT-2025-014",
    titulo: "Congresso Educadores de Impacto",
    tipo: "Congresso",
    dataInicio: "2025-09-20",
    dataFim: "2025-09-21",
    cidade: "Rio de Janeiro",
    estado: "RJ",
    local: "Centro de Convenções",
    resumo:
      "Conteúdos, palestras e networking para educadores e instrutores. Atualização e padronização profissional.",
    ano: 2025,
  },
  {
    id: "EVT-2024-008",
    titulo: "Encontro Nacional de Profissionais",
    tipo: "Encontro",
    dataInicio: "2024-06-05",
    cidade: "Fortaleza",
    estado: "CE",
    local: "Evento Regional",
    resumo:
      "Encontro institucional para troca de experiências, orientações e fortalecimento da rede federada.",
    ano: 2024,
  },
];

const tipos: EventoTipo[] = ["Premiação", "Congresso", "Capacitação", "Encontro", "Outro"];

const PremiacoesEventos: React.FC = () => {
  const [busca, setBusca] = useState("");
  const [ano, setAno] = useState<string>("");
  const [tipo, setTipo] = useState<string>("");
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [aberto, setAberto] = useState(false);
  const [selecionado, setSelecionado] = useState<Evento | null>(null);

  const anosDisponiveis = useMemo(() => {
    return Array.from(new Set(eventosMock.map((e) => e.ano))).sort((a, b) => b - a);
  }, []);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();

    return eventosMock.filter((e) => {
      const matchBusca =
        !q ||
        e.titulo.toLowerCase().includes(q) ||
        e.resumo.toLowerCase().includes(q) ||
        e.id.toLowerCase().includes(q) ||
        (e.cidade?.toLowerCase().includes(q) ?? false) ||
        (e.estado?.toLowerCase().includes(q) ?? false);

      const matchAno = !ano || String(e.ano) === ano;
      const matchTipo = !tipo || e.tipo === tipo;

      return matchBusca && matchAno && matchTipo;
    });
  }, [busca, ano, tipo]);

  const abrir = (e: Evento) => {
    setSelecionado(e);
    setAberto(true);
  };

  const fechar = () => {
    setAberto(false);
    setSelecionado(null);
  };

  const limpar = () => {
    setBusca("");
    setAno("");
    setTipo("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 text-gray-900">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950" />
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] bg-orange-500 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
            <img src="/icons/icon6.png" alt="Premiações e Eventos" className="w-6 h-6 object-contain" />
            <span className="text-xs md:text-sm font-bold text-amber-100 uppercase tracking-wider">
              Publicações
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white">
            Premiações e Eventos
          </h1>

          <p className="mt-5 text-base md:text-lg text-white/85 max-w-3xl mx-auto">
            Acompanhe premiações, congressos e encontros. Use a busca para encontrar por cidade, ano ou título.
          </p>

          <div className="mt-10 flex justify-center">
            <Link
              to="/contato"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all"
            >
              <span>Quero participar</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </section>

      {/* BUSCA + FILTROS */}
      <section className="py-10">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_25px_60px_-35px_rgba(0,0,0,.35)] p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por título, cidade, UF ou ID..."
                    className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                </div>

                <button
                  onClick={() => setMostrarFiltros((v) => !v)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all"
                >
                  <Filter className="w-5 h-5" />
                  Filtros
                </button>
              </div>

              {mostrarFiltros && (
                <div className="mt-5 grid md:grid-cols-3 gap-4">
                  <select
                    value={ano}
                    onChange={(e) => setAno(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-2xl bg-white"
                  >
                    <option value="">Todos os Anos</option>
                    {anosDisponiveis.map((a) => (
                      <option key={a} value={String(a)}>
                        {a}
                      </option>
                    ))}
                  </select>

                  <select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-2xl bg-white"
                  >
                    <option value="">Todos os Tipos</option>
                    {tipos.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={limpar}
                    className="px-4 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all"
                  >
                    Limpar
                  </button>
                </div>
              )}

              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm md:text-base text-gray-700">
                  <span className="font-black text-amber-700">{filtrados.length}</span> resultados
                </p>
              </div>
            </div>

            {/* GRID */}
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {filtrados.map((e) => (
                <button
                  key={e.id}
                  onClick={() => abrir(e)}
                  className="text-left bg-white rounded-3xl border border-amber-100/70 p-6 md:p-7 shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] hover:shadow-[0_28px_70px_-45px_rgba(0,0,0,.55)] transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <img src="/icons/icon6.png" alt="Evento" className="w-10 h-10 object-contain" />
                      </div>
                      <div>
                        <div className="text-xl md:text-2xl font-black text-gray-900">
                          {e.titulo}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <span className="font-semibold">ID:</span> {e.id} • {e.tipo}
                        </div>

                        <div className="mt-3 space-y-1 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span>
                              {e.dataInicio}
                              {e.dataFim ? ` → ${e.dataFim}` : ""}
                            </span>
                          </div>
                          {(e.cidade || e.estado) && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span>
                                {e.cidade ? e.cidade : "—"}{e.estado ? ` • ${e.estado}` : ""}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold">
                      Ver <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>

                  <div className="mt-4 text-gray-700 leading-relaxed">
                    {e.resumo}
                  </div>
                </button>
              ))}
            </div>

            {filtrados.length === 0 && (
              <div className="mt-8 text-center py-14 bg-white rounded-3xl border border-amber-100/70 shadow-sm">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center">
                  <img src="/icons/icon6.png" alt="Sem resultados" className="w-12 h-12 object-contain opacity-70" />
                </div>
                <div className="text-2xl font-black text-gray-900">Nada encontrado</div>
                <p className="mt-2 text-gray-600">Ajuste a busca ou remova filtros.</p>
                <button
                  onClick={limpar}
                  className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all"
                >
                  Limpar filtros
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MODAL */}
      {aberto && selecionado && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={fechar} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-white rounded-3xl border border-amber-100 shadow-2xl overflow-hidden">
              <div className="p-6 md:p-7 border-b border-gray-100 flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img src="/icons/icon6.png" alt="Evento" className="w-10 h-10 object-contain" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-gray-900">{selecionado.titulo}</div>
                    <div className="mt-1 text-sm text-gray-600">
                      <span className="font-semibold">ID:</span> {selecionado.id} • {selecionado.tipo}
                    </div>
                  </div>
                </div>

                <button
                  onClick={fechar}
                  className="w-10 h-10 rounded-2xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center"
                  aria-label="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 md:p-7">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-gray-200 p-4">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Data</div>
                    <div className="mt-1 font-extrabold text-gray-900">
                      {selecionado.dataInicio}
                      {selecionado.dataFim ? ` → ${selecionado.dataFim}` : ""}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Local</div>
                    <div className="mt-1 font-extrabold text-gray-900">
                      {selecionado.local ? selecionado.local : "A definir"}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {(selecionado.cidade || selecionado.estado)
                        ? `${selecionado.cidade ?? "—"}${selecionado.estado ? ` • ${selecionado.estado}` : ""}`
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-orange-50/40 p-5">
                  <div className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                    Sobre
                  </div>
                  <div className="mt-2 text-gray-700 leading-relaxed">
                    {selecionado.resumo}
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {selecionado.linkExterno ? (
                    <a
                      href={selecionado.linkExterno}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all"
                    >
                      Ver detalhes no site <ArrowRight className="w-5 h-5" />
                    </a>
                  ) : (
                    <div className="px-6 py-3 rounded-2xl border border-gray-200 text-gray-600 font-semibold text-center">
                      Link indisponível
                    </div>
                  )}

                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:shadow-lg transition-all"
                  >
                    Falar com a Federação <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              <div className="px-6 md:px-7 py-5 bg-gray-50 border-t border-gray-100 text-sm text-gray-600">
                Informações podem variar por edição. Em caso de dúvida, fale com a Federação.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiacoesEventos;
